import { useState } from 'react'
import { useDispatch } from 'react-redux'
import { z } from 'zod'
import { optionalPhone } from '@/lib/phone'
import { zodResolverI18n } from '@/lib/zod-resolver-i18n'
import { useTranslation } from 'react-i18next'
import { AlertCircleIcon } from 'lucide-react'
import {
	Alert, AlertDescription, AlertTitle,
	Form, FormControl, FormDialog, FormField, FormItem, FormLabel, FormMessage,
	Input, PhoneInput,
} from '@rentflowdev/ui'
import { dealsApi, useUpdateContactMutation } from '@/api'
import { extractErrorMessage } from '@/lib/error-message'
import { usePersistedForm } from '@/lib/use-persisted-form'
import type { Contact } from '@/types'

const editContactSchema = z.object({
	name:    z.string().trim().min(1, 'contacts.editDialog.validation.nameRequired'),
	phone:   optionalPhone('contacts.editDialog.validation.phoneInvalid'),
	email:   z.union([z.literal(''), z.string().email('contacts.editDialog.validation.emailInvalid')]).optional(),
	company: z.string().trim().optional(),
})

type EditContactFormValues = z.infer<typeof editContactSchema>

interface EditContactDialogProps {
	contact: Contact
	open: boolean
	onOpenChange: (open: boolean) => void
}

export function EditContactDialog({ contact, open, onOpenChange }: EditContactDialogProps) {
	const { t } = useTranslation()
	const dispatch = useDispatch()
	const [updateContact, { isLoading }] = useUpdateContactMutation()
	const [errorMsg, setErrorMsg] = useState<string | undefined>(undefined)
	// Decided once from the contact's stored phone (not reactively from the field
	// value): the masked PhoneInput fires onAccept on mount and rewrites whatever
	// it is given, so a value it cannot represent would be corrupted on open and
	// then propagated to every deal by the contact→deal sync.
	//
	// The gate is the CANONICAL shape (`+` + digits), not mere validity: a
	// valid-but-legacy `89991234567` is exactly what the mask mangles on mount
	// (digit shift, last digit dropped), and nothing guarantees the normalize
	// script has run before this code ships. Both mask branches round-trip
	// `+digits` verbatim, so canonical values are safe; everything else keeps
	// the free-form input.
	const [useMaskedPhone] = useState(
		() => !contact.phone || /^\+\d{8,15}$/.test(contact.phone),
	)

	const { form, resetDraft } = usePersistedForm<EditContactFormValues>({
		key:           `contact:edit:${contact.id}`,
		// Черновик, снятый до того, как контакт изменили где-то ещё, больше не
		// подставляется: компанию заводили на карточке арендатора, а здесь
		// поле открывалось пустым — и сохранение стирало её обратно.
		recordVersion: contact.updatedAt,
		defaultValues: {
			name:    contact.name,
			phone:   contact.phone ?? '',
			email:   contact.email ?? '',
			company: contact.company ?? '',
		},
		resolver: zodResolverI18n(editContactSchema),
		mode:     'onBlur',
		active:   open,
	})

	async function onSubmit(values: EditContactFormValues) {
		setErrorMsg(undefined)
		try {
			await updateContact({
				id: contact.id,
				body: {
					// Пустая строка, а не undefined: undefined значит «не трогать»,
					// и стереть компанию, телефон или почту было невозможно —
					// поле очищали, сохраняли, и старое значение возвращалось.
					name:    values.name,
					phone:   values.phone,
					email:   values.email,
					company: values.company,
				},
			}).unwrap()
			// Deals denormalize the contact's name/company/phone/email onto themselves
			// (see schema comment on Deal.clientCompany); the backend keeps those in sync
			// on this same update, but contactsApi and dealsApi are separate RTK Query
			// slices, so any deal cards for this contact need an explicit refetch.
			dispatch(dealsApi.util.invalidateTags(['Deal']))
			resetDraft()
			onOpenChange(false)
		} catch (err) {
			setErrorMsg(extractErrorMessage(err))
		}
	}

	return (
		<Form {...form}>
			<FormDialog
				open={open}
				onOpenChange={onOpenChange}
				title={t('contacts.editDialog.title')}
				onSubmit={form.handleSubmit(onSubmit)}
				submitLabel={isLoading ? t('common.saving') : t('contacts.editDialog.save')}
				cancelLabel={t('deals.createDialog.cancel')}
				closeAriaLabel={t('common.close')}
				isSubmitting={isLoading}
			>
				{errorMsg !== undefined && (
					<Alert variant="destructive">
						<AlertCircleIcon className="size-4" />
						<AlertTitle>{t('contacts.editDialog.errors.save')}</AlertTitle>
						{errorMsg && <AlertDescription>{errorMsg}</AlertDescription>}
					</Alert>
				)}
				<FormField
					control={form.control}
					name="name"
					render={({ field }) => (
						<FormItem>
							<FormLabel>{t('contacts.editDialog.name')}</FormLabel>
							<FormControl>
								<Input placeholder={t('contacts.editDialog.namePlaceholder')} {...field} />
							</FormControl>
							<FormMessage />
						</FormItem>
					)}
				/>
				<FormField
					control={form.control}
					name="company"
					render={({ field }) => (
						<FormItem>
							<FormLabel>{t('contacts.editDialog.company')}</FormLabel>
							<FormControl>
								<Input placeholder={t('contacts.editDialog.companyPlaceholder')} {...field} />
							</FormControl>
							<FormMessage />
						</FormItem>
					)}
				/>
				<FormField
					control={form.control}
					name="phone"
					render={({ field }) => (
						<FormItem>
							<FormLabel>{t('contacts.editDialog.phone')}</FormLabel>
							<FormControl>
								{useMaskedPhone ? (
									<PhoneInput value={field.value ?? ''} onChange={field.onChange} />
								) : (
									<Input {...field} value={field.value ?? ''} />
								)}
							</FormControl>
							{/* Without this the phone rule blocks Save with nothing on
							    screen: a legacy free-form number fails validation and
							    the dialog just silently refuses to close. */}
							<FormMessage />
						</FormItem>
					)}
				/>
				<FormField
					control={form.control}
					name="email"
					render={({ field }) => (
						<FormItem>
							<FormLabel>{t('contacts.editDialog.email')}</FormLabel>
							<FormControl>
								<Input type="email" placeholder="name@example.com" {...field} />
							</FormControl>
							<FormMessage />
						</FormItem>
					)}
				/>
			</FormDialog>
		</Form>
	)
}
